import { isTicDisabledForEnvironment } from '@timelyapp/integration-cloud-sdk/api/api.environment.utils';
import { DEFAULT_USER_PERMISSIONS } from 'constants/permissions';
import {
  type AutosheetView,
  AutosheetViewSchema,
  HAS_ENABLED_TIC,
  MEMORY_TOUR_COMPLETED,
  PREFERRED_AUTOSHEET_VIEW,
  PREFERRED_HOURS_VIEW,
  TIC_MEMORY_VIEWMODE,
  type TicMemoryViewmode,
  TicMemoryViewmodeSchema,
} from 'constants/user';
import { buildEnrichedUser } from 'factories/users';
import { isEqual } from 'lodash-es';
import { getIsTicServiceOnline } from 'redux/modules/tic/tic.global';
import type {
  AllAcceptedUserPropertyKey,
  UserPermissionAccessor,
  UserPermissionsFacade,
} from 'redux/modules/user.types';
import {
  UserPermission,
  UserPermissionResource,
  UserPermissionResourceAlias,
} from 'redux/modules/user.types';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import { RoleName } from 'types/role';
import type { EnrichedUser, User, UserTimeFormat } from 'types/user';
import {
  getAccountFeatures,
  getAccountOwnerId,
  getCurrentAccount,
  getCurrentAccountId,
  getHasTicIntegrationsSupport,
  getHasTimesheetAssistantV2Feature,
  getHideSwitchToClassicFeature,
} from './accounts';
import { getPretendToBeNonMemoryUser } from './development';
import { getUserRoleById } from './roles';

const readUserStore = (state: IState) => state.user;
const readDevelopmentStatus = () => !!__DEVELOPMENT__;
const readUserPermissions = (state: IState) => state.user.permissions;
const readEmulatedPermissions = (state: IState) => state.user.emulatedPermissions;
const readUserXhrStatus = (state: IState) => state.user.xhrStatus;
const readUserEmail = (state: IState) => state.user.email;

export const getUserPropertiesXhrStatus = createSelector(
  [readUserXhrStatus],
  xhrStatus => xhrStatusUIState(xhrStatus, 'user-properties'),
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

export const getUserStore = readUserStore;

export const getUser = createSelector([getUserStore], (userStore): User => {
  const {
    force_tour,
    accountSettings,
    permissions,
    emulatedPermissions,
    emulatedRoleId,
    showDevtools,
    ...rest
  } = userStore;

  return {
    ...rest,
    time_format: +userStore.time_format! as UserTimeFormat, // Wrong type is set by the boostrapped data atm, need this as int
    role_id: userStore.emulatedRoleId || userStore.role_id,
  };
});

export const getUserHideHourlyRate = createSelector([getUser], user => user.hide_hourly_rate);

export const getEnrichedUser = createSelector(
  [getUser, getUserRoleById],
  (user, roleById): EnrichedUser => buildEnrichedUser(user, roleById)
);

export const getIsTourForced = createSelector([getUserStore], userStore => !!userStore.force_tour);

export const getIsUserMemoryTourCompleted = createSelector(
  [getUserStore],
  (userStore): boolean => !!(userStore.properties[MEMORY_TOUR_COMPLETED] || false)
);

export const getUserId = createSelector([getUser], (user): number => user.id);

export const getUserTimeFormat = createSelector(
  [getUser],
  (user): UserTimeFormat => user.time_format || 24
);

export const getUserProperties = createSelector([getUserStore], userStore => userStore.properties);

export const getUserPropertyAsBoolean = (
  state: IState,
  propertyKey: AllAcceptedUserPropertyKey
) => {
  const item = state.user.properties[propertyKey];

  if (item === undefined || item === null) {
    return false;
  }
  if (typeof item === 'boolean') {
    return item;
  }

  return false;
};

export const getUserPropertyAsNumber = (
  state: IState,
  propertyKey: AllAcceptedUserPropertyKey
): number => {
  const item = state.user.properties[propertyKey];
  return !item || isNaN(+item) ? 0 : +item;
};

let __TIMELY_DEV__IMPERSONATE_EMPLOYEE = false;
try {
  __TIMELY_DEV__IMPERSONATE_EMPLOYEE = !!localStorage.getItem('__TIMELY_DEV__IMPERSONATE_EMPLOYEE');
} catch {}

export const getUserEmail = readUserEmail;

export const getIsUserTimelyEmployee = createSelector([readUserEmail], (email): boolean => {
  if (!email) return false;

  if (email === 'ava@timelyapp.com') {
    return false; // Layal's video production user
  }

  // Memory employee emails
  return (
    email.endsWith('@timelyapp.com') ||
    email.endsWith('@memory.ai') ||
    email.endsWith('@timely.com') ||
    __TIMELY_DEV__IMPERSONATE_EMPLOYEE
  );
});

export const getIsUserInExperimentalFeatureGroup = createSelector(
  [getIsUserTimelyEmployee, readDevelopmentStatus, getPretendToBeNonMemoryUser],
  (isMemoryEmployee, appIsRunningInDevelopment, pretendToBeNonMemoryUser): boolean => {
    if (pretendToBeNonMemoryUser) {
      return false;
    }
    return isMemoryEmployee || appIsRunningInDevelopment;
  }
);

export const getCanUseTimesheetAssistantV2 = createSelector(
  [getHasTimesheetAssistantV2Feature],
  (hasFeatureFlag: boolean): boolean => hasFeatureFlag
);

export const getIsUserAdmin = createSelector([getUser], user => user.admin);

const readUserGlobalPermissions = (state: IState) => state.user.globalPermissions;

export const getHasGlobalProjectRead = createSelector(
  [readUserGlobalPermissions],
  globalPermissions => !!globalPermissions?.project?.includes(UserPermission.Read)
);

export const getIsUserInTutorial = createSelector(
  [getIsTourForced, getIsUserMemoryTourCompleted],
  (isTourForced, tourCompleted) => {
    return !!isTourForced && !tourCompleted;
  }
);

export const getIsEmulatingPermissions = createSelector(
  [readEmulatedPermissions],
  emulatedPermissions => !!emulatedPermissions
);

export const getRawUserPermissions = createSelector(
  [readUserPermissions, readEmulatedPermissions],
  (permissions, emulatedPermissions) =>
    emulatedPermissions ?? permissions ?? DEFAULT_USER_PERMISSIONS
);

export const getUserPermissions = createSelector(
  [getRawUserPermissions],
  (permissions): UserPermissionsFacade => {
    const resourceValues = Object.values(UserPermissionResource);
    const createPermissionAccessor = (permission: UserPermission) =>
      resourceValues.reduce((accessor, resource) => {
        accessor[UserPermissionResourceAlias[resource]] =
          !!permissions[resource]?.includes(permission);
        return accessor;
      }, {} as UserPermissionAccessor);
    const createSingletonPermission = (
      resource: UserPermissionResource,
      permission: UserPermission
    ) => !!permissions[resource]?.includes(permission);

    return {
      canCreate: createPermissionAccessor(UserPermission.Create),
      canRead: createPermissionAccessor(UserPermission.Read),
      canUpdate: createPermissionAccessor(UserPermission.Update),
      canDelete: createPermissionAccessor(UserPermission.Delete),
      canNotify: createPermissionAccessor(UserPermission.Notify),
      canChangeOwnershipOf: createPermissionAccessor(UserPermission.ChangeOwnership),
      canManageSubscriptions: createSingletonPermission(
        UserPermissionResource.Account,
        UserPermission.ManageSubscriptions
      ),
      canManageOauthApplications: createSingletonPermission(
        UserPermissionResource.Account,
        UserPermission.ManageDev
      ),
      canUpgradeSubscription: createSingletonPermission(
        UserPermissionResource.Account,
        UserPermission.ManagePlan
      ),
      canContactAdmin: createSingletonPermission(
        UserPermissionResource.Account,
        UserPermission.ContactAdmin
      ),
      canContactManager: createSingletonPermission(
        UserPermissionResource.Account,
        UserPermission.ContactManager
      ),
      canLockDays: createSingletonPermission(
        UserPermissionResource.DayProperty,
        UserPermission.Create
      ),
      canUnlockDays: createSingletonPermission(
        UserPermissionResource.DayProperty,
        UserPermission.Update
      ),
    };
  }
);

export const getHasAccessToDevtols = createSelector(
  [getIsUserInExperimentalFeatureGroup, getPretendToBeNonMemoryUser],
  (experimental, pretendingToNotBeExperimental) => experimental || pretendingToNotBeExperimental
);

const readShowDevTools = (state: IState) => state.user.showDevtools;
export const getShowDevtools = createSelector(
  [readShowDevTools, getHasAccessToDevtols],
  (show, hasAccess) => show && hasAccess
);

export const getIsAdminOwner = createSelector([getUser, getAccountOwnerId], (user, ownerId) => {
  return user.id === ownerId;
});

export const getHasAccessToInvoices = createSelector(
  [getAccountFeatures, getUserPermissions],
  (features, permissions) => {
    return !!(permissions.canRead.invoices && features.invoices);
  }
);

export const getPreferredHoursView = createSelector(
  [getUserProperties, getCurrentAccountId],
  (props, currentAccountId) => {
    // Nubank specific
    if (currentAccountId === 1124026) {
      return props[PREFERRED_HOURS_VIEW] ?? 'timesheet';
    }
    return props[PREFERRED_HOURS_VIEW] ?? 'timeline';
  }
);

export const getPreferredAutosheetView = createSelector(
  [getUserProperties],
  (props): AutosheetView =>
    AutosheetViewSchema.safeParse(props[PREFERRED_AUTOSHEET_VIEW]).data ?? 'day'
);

/**
 * Selector for checking if TIC is disabled for the current environment.
 */
const getIsTicDisabledForEnvironment = createSelector([(state: IState) => state], () =>
  isTicDisabledForEnvironment()
);

/**
 * Returns true when user has opted into TIC,
 * UNLESS TIC services are offline (e.g, maintenance).
 */
export const getHasEnabledTic = createSelector(
  [getUserProperties, getIsTicServiceOnline, getIsTicDisabledForEnvironment],
  (props, ticServicesAreOnline, isTicDisabled) => {
    if (!ticServicesAreOnline) return false;
    return !!props[HAS_ENABLED_TIC] && !isTicDisabled;
  }
);

/**
 * Returns true when TIC API access is needed:
 * - User opted into TIC timeline (`has_enabled_tic`)
 * - Workspace has `tic_integrations` feature (TT-4802: enables Next-Gen integrations without timeline opt-in)
 */
export const getShouldAuthenticateToTic = createSelector(
  [getHasEnabledTic, getHasTicIntegrationsSupport],
  (hasEnabledTic, hasTicIntegrations) => hasEnabledTic || hasTicIntegrations
);

export const getTicMemoryViewmode = createSelector(
  [getUserProperties],
  (properties): TicMemoryViewmode =>
    TicMemoryViewmodeSchema.safeParse(properties[TIC_MEMORY_VIEWMODE]).data ?? 'timeline'
);

export const getUserRoleId = createSelector(
  [getUserStore],
  user => user.emulatedRoleId || user.role_id
);

export const getUserRoleName = createSelector(
  [getUserRoleById, getUserRoleId],
  (userRole, userRoleId) => {
    return userRole[userRoleId]?.name;
  }
);

export const getIsUserEmployee = createSelector([getUserRoleName], userRoleName => {
  return userRoleName === RoleName.Employee;
});

export const getIsUserManager = createSelector([getUserRoleName], userRoleName => {
  return userRoleName === RoleName.Manager;
});

/**
 * Returns true if user can opt out to old timeline.
 * Workspaces created on/after Nov 27, 2025 or with tic_force_enable cannot opt out.
 */
export const getCanOptOutToOldTimeline = createSelector([getCurrentAccount], (account): boolean => {
  if (account.tic_force_enable) return false;
  return (
    !!account.created_at && account.created_at < new Date('2025-11-27T00:00:00Z').getTime() / 1000
  );
});

/**
 * Returns true if the workspace allows switching from AutoSheet to Classic view.
 * Workspaces created on/after March 3, 2026 cannot switch to classic.
 * Also respects the hide_switch_to_classic feature flag which can disable the button.
 */
export const getCanSwitchToClassicView = createSelector(
  [getCurrentAccount, getHideSwitchToClassicFeature],
  (account, hideSwitchToClassic): boolean => {
    if (process.env.NODE_ENV === 'development') {
      return true; // Don't restrict in development to allow testing
    }
    if (hideSwitchToClassic) {
      return false;
    }
    return (
      !!account.created_at && account.created_at < new Date('2026-03-03T00:00:00Z').getTime() / 1000
    );
  }
);
